import React, { useState } from 'react';
import { Drawer, Table, Input, DatePicker, Select, Button, Space, Typography, Tag, Tooltip, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useGetExecuteQueryLogsQuery } from '../../../store/rtkQuery/utility';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

interface ExecutionLogsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ExecutionLogsDrawer: React.FC<ExecutionLogsDrawerProps> = ({ open, onClose }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [fromDate, setFromDate] = useState<string | undefined>(undefined);
  const [toDate, setToDate] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<'desc' | 'asc'>('desc');

  const { data, isFetching, refetch } = useGetExecuteQueryLogsQuery(
    { page, pageSize, search, fromDate, toDate, sortBy },
    { skip: !open }
  );

  const logs = data?.data?.items || [];
  const total = data?.data?.total || 0;

  const handleSearch = debounce((value: string) => {
    setSearch(value || undefined);
    setPage(1);
  }, 500);

  const handleDateRange = (dates: any, dateStrings: [string, string]) => {
    if (dates) {
      setFromDate(dateStrings[0]);
      setToDate(dateStrings[1]);
    } else {
      setFromDate(undefined);
      setToDate(undefined);
    }
    setPage(1);
  };

  const columns = [
    {
      title: 'Executed By',
      dataIndex: 'executedBy',
      key: 'executedBy',
      width: 150,
      render: (text: string) => <Text strong>{text || 'System'}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: any, record: any) => {
        const hasError = !!record.error;
        return hasError ? (
          <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>
        ) : (
          <Tag icon={<CheckCircleOutlined />} color="success">Success</Tag>
        );
      }
    },
    {
      title: 'Query Snippet',
      key: 'query',
      render: (_: any, record: any) => {
        const rawQuery = record.query?.rawQuery || '';
        return (
          <Tooltip title={rawQuery} placement="topLeft">
            <div className="truncate max-w-[300px] font-mono text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200">
              {rawQuery}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Executed At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => dayjs(date).format('DD MMM YYYY, hh:mm A'),
    }
  ];

  const expandedRowRender = (record: any) => {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-md shadow-inner">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text strong className="text-gray-700">Full SQL Query:</Text>
            <pre className="mt-2 bg-gray-800 text-green-400 p-3 rounded-md overflow-x-auto text-xs font-mono">
              {record.query?.rawQuery || 'N/A'}
            </pre>
          </Col>
          {record.error ? (
            <Col span={24}>
              <Text strong className="text-red-600">Error Message:</Text>
              <pre className="mt-2 bg-red-50 text-red-600 border border-red-200 p-3 rounded-md overflow-x-auto text-xs font-mono whitespace-pre-wrap">
                {typeof record.error === 'object' ? JSON.stringify(record.error, null, 2) : record.error}
              </pre>
            </Col>
          ) : (
            <Col span={24}>
              <Text strong className="text-gray-700">Response Data:</Text>
              <pre className="mt-2 bg-white border border-gray-300 text-gray-800 p-3 rounded-md overflow-x-auto text-xs font-mono max-h-[300px] overflow-y-auto">
                {JSON.stringify(record.response, null, 2)}
              </pre>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <span>SQL Execution Logs</span>
          <Button type="text" icon={<ReloadOutlined />} onClick={refetch} loading={isFetching}>
            Refresh
          </Button>
        </div>
      }
      placement="right"
      width={900}
      onClose={onClose}
      open={open}
      destroyOnClose
      bodyStyle={{ padding: '16px 24px', backgroundColor: '#f9fafb' }}
    >
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-4">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={8}>
            <Input
              placeholder="Search by name or error..."
              prefix={<SearchOutlined className="text-gray-400" />}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              className="w-full"
            />
          </Col>
          <Col xs={24} sm={10} md={10}>
            <RangePicker 
              className="w-full" 
              onChange={handleDateRange}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={6} md={6}>
            <Select 
              value={sortBy} 
              onChange={(val) => setSortBy(val)} 
              className="w-full"
              options={[
                { value: 'desc', label: 'Newest First' },
                { value: 'asc', label: 'Oldest First' }
              ]}
            />
          </Col>
        </Row>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={isFetching}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => true,
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
            },
          }}
          size="middle"
        />
      </div>
    </Drawer>
  );
};

export default ExecutionLogsDrawer;
